import { useEffect, useRef } from 'react';
import {
    normalizePhone,
    phonesMatch,
    useSmsRealtimeStore,
} from '../store/smsRealtimeStore';

function applyStatusToChat(setChat, event) {
    if (event.messageId == null) {
        return;
    }
    setChat((prev) => {
        if (!Array.isArray(prev) || prev.length === 0) {
            return prev;
        }
        let changed = false;
        const next = prev.map((msg) => {
            if (msg.id != null && Number(msg.id) === Number(event.messageId)) {
                const nextStatus = event.status;
                if (!nextStatus || String(msg.status || '').toUpperCase() === String(nextStatus).toUpperCase()) {
                    return msg;
                }
                changed = true;
                return { ...msg, status: nextStatus };
            }
            return msg;
        });
        return changed ? next : prev;
    });
}

function applyIncomingToChat(setChat, event) {
    setChat((prev) => {
        const list = Array.isArray(prev) ? prev : [];
        if (event.messageId != null) {
            const exists = list.some(
                (m) => m.id != null && Number(m.id) === Number(event.messageId)
            );
            if (exists) {
                return list;
            }
        }
        const meta = list.find((m) => m?.customerFirstName || m?.customerLastName);
        return [
            ...list.filter((m) => m && m.message),
            {
                id: event.messageId,
                business: event.business,
                customer: event.customer,
                message: event.message,
                customerFirstName:
                    event.customerFirstName || meta?.customerFirstName || '',
                customerLastName:
                    event.customerLastName || meta?.customerLastName || '',
                customerDob: meta?.customerDob || '',
                messageType: event.messageType || 'INCOMING',
                status: event.status || 'UNREAD',
                modifiedAt: event.modifiedAt,
            },
        ];
    });
}

function applyIncomingToInbox(setUsers, event, isOpen) {
    const customer = event.customer;
    setUsers((prevUsers) => {
        if (!Array.isArray(prevUsers)) {
            return prevUsers;
        }
        const idx = prevUsers.findIndex((u) =>
            phonesMatch(u.customerNumber, customer)
        );

        if (idx === -1) {
            const names = [event.customerFirstName, event.customerLastName]
                .filter(Boolean)
                .join(' ')
                .trim();
            const newRow = {
                customerNumber: normalizePhone(customer) || customer,
                customerNames: names || customer,
                lastMessage: event.message,
                createdAt: event.createdAt || new Date().toISOString(),
                unreadMessage: isOpen ? 0 : 1,
            };
            return [newRow, ...prevUsers];
        }

        const existing = prevUsers[idx];
        const updated = {
            ...existing,
            lastMessage: event.message,
            createdAt: event.createdAt || existing.createdAt || new Date().toISOString(),
            unreadMessage: isOpen
                ? 0
                : (Number(existing.unreadMessage) || 0) + 1,
            customerNames:
                existing.customerNames ||
                [event.customerFirstName, event.customerLastName]
                    .filter(Boolean)
                    .join(' ')
                    .trim() ||
                existing.customerNames,
        };
        const rest = prevUsers.filter((_, i) => i !== idx);
        return [updated, ...rest];
    });
}

/**
 * Applies Socket.IO SMS events to inbox + open conversation state.
 * Safe no-ops when event is unrelated; dedupes inbound by messageId.
 */
export function useSmsRealtimeSync({
    selectedUser,
    setChat,
    setUsers,
    onReconnect,
}) {
    const eventSeq = useSmsRealtimeStore((s) => s.eventSeq);
    const drainEvents = useSmsRealtimeStore((s) => s.drainEvents);
    const reconnectSeq = useSmsRealtimeStore((s) => s.reconnectSeq);
    const processedIdsRef = useRef(new Set());
    const selectedUserRef = useRef(selectedUser);
    const onReconnectRef = useRef(onReconnect);

    selectedUserRef.current = selectedUser;
    onReconnectRef.current = onReconnect;

    const rememberId = (id) => {
        if (id == null || Number.isNaN(id)) {
            return false;
        }
        if (processedIdsRef.current.has(id)) {
            return true;
        }
        processedIdsRef.current.add(id);
        if (processedIdsRef.current.size > 500) {
            const first = processedIdsRef.current.values().next().value;
            processedIdsRef.current.delete(first);
        }
        return false;
    };

    useEffect(() => {
        if (reconnectSeq <= 1) {
            return;
        }
        if (typeof onReconnectRef.current === 'function') {
            onReconnectRef.current();
        }
    }, [reconnectSeq]);

    useEffect(() => {
        if (eventSeq === 0) {
            return;
        }

        const events = drainEvents();
        if (!events.length) {
            return;
        }

        events.forEach((event) => {
            const type = event.notificationType;

            if (type === 'SMS_STATUS') {
                applyStatusToChat(setChat, event);
                return;
            }

            if (type !== 'SMS_RECEIVED') {
                return;
            }

            if (rememberId(event.messageId)) {
                return;
            }

            const customer = event.customer;
            if (!customer) {
                return;
            }

            const isOpen = phonesMatch(selectedUserRef.current, customer);
            if (isOpen) {
                applyIncomingToChat(setChat, event);
            }
            applyIncomingToInbox(setUsers, event, isOpen);
        });
    }, [eventSeq, drainEvents, setChat, setUsers]);
}

export default useSmsRealtimeSync;
